import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./ProjectCandidate.css";
import ProjectTabs from "./ProjectTabs";
import { API_BASE } from "../../../ApiUrl";

const API_URL = `${API_BASE}/disclosure-requests/`;
const TENANT_RECORDS_API_URL = `${API_BASE}/tenant-records/`;
const PROJECTS_API_URL = `${API_BASE}/projects/`;
const PROFESSIONAL_PROFILE_API_URL = `${API_BASE}/professional-profiles/`;

const emptyRequest = (project, professional) => ({
  id: null,
  project: project || "",
  professional: professional || "",
  purpose: "",
  requested_fields: [],
  field_input: ""
});

const AddDisclosureRequest = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const prefill = location.state?.prefill || null;
  const prefilledProject = prefill?.project ? String(prefill.project) : "";
  const prefilledProfessional = prefill?.professional
    ? String(prefill.professional)
    : "";

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState([
    emptyRequest(prefilledProject, prefilledProfessional)
  ]);

  const [tenantId, setTenantId] = useState(null);
  const [userId, setUserId] = useState(null);

  const [projectLabel, setProjectLabel] = useState("");
  const [professionalLabel, setProfessionalLabel] = useState("");

  const getTenantUser = () => {
    try {
      const tenantUser = localStorage.getItem("tenant_user");
      return tenantUser ? JSON.parse(tenantUser) : null;
    } catch (error) {
      console.error("Error reading tenant_user:", error);
      return null;
    }
  };

  useEffect(() => {
    const tenantUser = getTenantUser();
    if (tenantUser?.tenant_id) setTenantId(Number(tenantUser.tenant_id));
    if (tenantUser?.user_id) setUserId(Number(tenantUser.user_id));
  }, []);

  useEffect(() => {
    if (tenantId) fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const fetchRequests = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        TENANT_RECORDS_API_URL,
        {
          tenant_id: tenantId,
          tables: ["disclosure_requests"]
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const result = response.data;

      let rows = [];
      if (result?.data?.disclosure_requests) {
        rows = result.data.disclosure_requests;
      } else if (result?.disclosure_requests) {
        rows = result.disclosure_requests;
      } else if (Array.isArray(result?.data)) {
        rows = result.data;
      } else if (Array.isArray(result)) {
        rows = result;
      }

      setRequests(rows);
    } catch (error) {
      console.error("Error fetching disclosure requests:", error);
      Swal.fire({
        icon: "error",
        title: "Unable to load requests",
        text:
          error.response?.data?.detail ||
          "Something went wrong while loading requests."
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchLabels = async () => {
      if (prefilledProject) {
        try {
          const res = await axios.get(
            `${PROJECTS_API_URL}${prefilledProject}/`
          );
          const data = res.data?.data || res.data;
          setProjectLabel(
            data?.project_name || data?.name || `Project ${prefilledProject}`
          );
        } catch {
          setProjectLabel(`Project ${prefilledProject}`);
        }
      }

      if (prefilledProfessional) {
        try {
          const res = await axios.get(
            `${PROFESSIONAL_PROFILE_API_URL}${prefilledProfessional}/`
          );
          const data = res.data?.data || res.data;
          const first = data?.first_name || "";
          const last = data?.last_name || "";
          const full = `${first} ${last}`.trim();
          setProfessionalLabel(
            full || data?.full_name || `Professional ${prefilledProfessional}`
          );
        } catch {
          setProfessionalLabel(`Professional ${prefilledProfessional}`);
        }
      }
    };

    fetchLabels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilledProject, prefilledProfessional]);

  // ---- Form handlers ----
  const handlePurposeChange = (index, value) => {
    setFormData((prev) =>
      prev.map((r, i) => (i === index ? { ...r, purpose: value } : r))
    );
  };

  const handleFieldInputChange = (index, value) => {
    setFormData((prev) =>
      prev.map((r, i) => (i === index ? { ...r, field_input: value } : r))
    );
  };

  const addFieldChip = (index) => {
    setFormData((prev) =>
      prev.map((r, i) => {
        if (i !== index) return r;
        const raw = (r.field_input || "").trim();
        if (!raw) return { ...r, field_input: "" };
        if (r.requested_fields.includes(raw)) {
          return { ...r, field_input: "" };
        }
        return {
          ...r,
          requested_fields: [...r.requested_fields, raw],
          field_input: ""
        };
      })
    );
  };

  const removeFieldChip = (index, fieldValue) => {
    setFormData((prev) =>
      prev.map((r, i) =>
        i === index
          ? {
              ...r,
              requested_fields: r.requested_fields.filter(
                (f) => f !== fieldValue
              )
            }
          : r
      )
    );
  };

  const handleFieldKeyDown = (index, e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addFieldChip(index);
    } else if (e.key === "Backspace" && !e.target.value) {
      setFormData((prev) =>
        prev.map((r, i) => {
          if (i !== index) return r;
          if (!r.requested_fields.length) return r;
          return {
            ...r,
            requested_fields: r.requested_fields.slice(0, -1)
          };
        })
      );
    }
  };

  // ---- Row handlers ----
  const handleAdd = () => {
    setFormData([emptyRequest(prefilledProject, prefilledProfessional)]);
    setIsEditing(false);
    setEditingId(null);
    setShowForm(true);
  };

  const handleAddAnother = () => {
    setFormData((prev) => [
      ...prev,
      emptyRequest(prefilledProject, prefilledProfessional)
    ]);
  };

  const handleRemoveForm = (index) => {
    setFormData((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEdit = (r) => {
    let fieldsArr = [];
    if (Array.isArray(r.requested_fields)) {
      fieldsArr = r.requested_fields.map((f) => String(f));
    } else if (typeof r.requested_fields === "string" && r.requested_fields) {
      try {
        const parsed = JSON.parse(r.requested_fields);
        if (Array.isArray(parsed)) fieldsArr = parsed.map((f) => String(f));
      } catch {
        fieldsArr = r.requested_fields
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
    }

    setFormData([
      {
        id: r.id,
        project: r.project ? String(r.project) : prefilledProject,
        professional: r.professional
          ? String(r.professional)
          : prefilledProfessional,
        purpose: r.purpose || "",
        requested_fields: fieldsArr,
        field_input: ""
      }
    ]);
    setEditingId(r.id);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = async (r) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete Request?",
      text: "Are you sure you want to delete this disclosure request?",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626"
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${API_URL}${r.id}/`);

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Disclosure request deleted successfully.",
        timer: 1500,
        showConfirmButton: false
      });

      fetchRequests();
    } catch (error) {
      console.error("Error deleting disclosure request:", error);
      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text:
          error.response?.data?.detail ||
          "Unable to delete the request."
      });
    }
  };

  // ---- Validation ----
  const validateRow = (r, index) => {
    const label = `Request ${index + 1}`;

    if (!r.project) {
      Swal.fire({
        icon: "warning",
        title: "Project Missing",
        text: `Project is missing for ${label}. Please go back and try again.`
      });
      return false;
    }

    if (!r.professional) {
      Swal.fire({
        icon: "warning",
        title: "Professional Missing",
        text: `Professional is missing for ${label}. Please go back and try again.`
      });
      return false;
    }

    if (!r.purpose.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Purpose Required",
        text: `Please provide a Purpose for ${label}.`
      });
      return false;
    }

    if (!Array.isArray(r.requested_fields) || !r.requested_fields.length) {
      Swal.fire({
        icon: "warning",
        title: "No Fields Selected",
        text: `Please add at least one requested field for ${label}.`
      });
      return false;
    }

    return true;
  };

  const buildPayload = (r) => ({
    project: Number(r.project),
    professional: Number(r.professional),
    requested_by: userId,
    purpose: r.purpose.trim(),
    requested_fields: r.requested_fields,
    tenant: tenantId
  });

  const extractError = (error) => {
    if (error.response?.data) {
      const d = error.response.data;
      if (typeof d === "string") return d;
      if (d.detail) return d.detail;
      if (d.message) return d.message;
      if (d.errors) {
        return Object.entries(d.errors)
          .map(
            ([field, errors]) =>
              `${field}: ${Array.isArray(errors) ? errors.join(", ") : errors}`
          )
          .join(" | ");
      }
      return JSON.stringify(d);
    }
    return error.message || "Unknown error";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tenantId || !userId) {
      Swal.fire({
        icon: "error",
        title: "Session missing",
        text: "Tenant ID or User ID is missing."
      });
      return;
    }

    const committed = formData.map((r) => {
      const pending = (r.field_input || "").trim();
      if (pending && !r.requested_fields.includes(pending)) {
        return {
          ...r,
          requested_fields: [...r.requested_fields, pending],
          field_input: ""
        };
      }
      return { ...r, field_input: "" };
    });

    setFormData(committed);

    for (let i = 0; i < committed.length; i++) {
      if (!validateRow(committed[i], i)) return;
    }

    const activeRows = committed.filter((r) => r.project && r.professional);

    const CONFIG = {
      headers: { "Content-Type": "application/json" }
    };

    try {
      setLoading(true);

      if (isEditing) {
        const payload = buildPayload(committed[0]);
        await axios.put(`${API_URL}${editingId}/`, payload, CONFIG);

        await Swal.fire({
          icon: "success",
          title: "Updated Successfully",
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        for (const r of activeRows) {
          await axios.post(API_URL, buildPayload(r), CONFIG);
        }

        await Swal.fire({
          icon: "success",
          title: "Created Successfully",
          text: `${activeRows.length} disclosure request${
            activeRows.length > 1 ? "s" : ""
          } created successfully.`,
          timer: 1500,
          showConfirmButton: false
        });
      }

      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      setFormData([emptyRequest(prefilledProject, prefilledProfessional)]);

      await fetchRequests();
    } catch (error) {
      console.error("Error saving disclosure request:", error);
      Swal.fire({
        icon: "error",
        title: isEditing ? "Update failed" : "Creation failed",
        text: extractError(error)
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setIsEditing(false);
    setEditingId(null);
    setFormData([emptyRequest(prefilledProject, prefilledProfessional)]);
  };

  // ---- Lookup helpers ----
  const getProjectName = (id) => {
    if (projectLabel && String(id) === prefilledProject) return projectLabel;
    return id ? `Project ${id}` : "-";
  };

  const getProfessionalName = (id) => {
    if (professionalLabel && String(id) === prefilledProfessional)
      return professionalLabel;
    return id ? `Professional ${id}` : "-";
  };

  const getFieldsArray = (r) => {
    if (Array.isArray(r.requested_fields)) return r.requested_fields;
    if (typeof r.requested_fields === "string" && r.requested_fields) {
      try {
        const parsed = JSON.parse(r.requested_fields);
        if (Array.isArray(parsed)) return parsed;
      } catch {
        return r.requested_fields
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
    }
    return [];
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="tenant-membership-page">
          <ProjectTabs />

          {!showForm ? (
            <>
              <div className="tenant-membership-page-header">
                <div>
                  <h2>Disclosure Requests</h2>
                </div>

                <div className="candidate-view-header-actions">
                  <button
                    type="button"
                    className="candidate-view-back-btn"
                    onClick={() => navigate(-1)}
                  >
                    ← Back
                  </button>

                  <button
                    type="button"
                    className="tenant-membership-add-top-btn"
                    onClick={handleAdd}
                  >
                    + Add Request
                  </button>
                </div>
              </div>

              <div className="tenant-membership-list-card">
                {loading ? (
                  <div className="tenant-membership-loading">
                    Loading requests...
                  </div>
                ) : requests.length === 0 ? (
                  <div className="tenant-membership-empty">
                    <div className="tenant-membership-empty-icon">+</div>
                    <h3>No Disclosure Requests Found</h3>
                    <p>
                      There are no disclosure requests available for the
                      current tenant.
                    </p>
                    <button
                      type="button"
                      className="tenant-membership-empty-add-btn"
                      onClick={handleAdd}
                    >
                      + Add Request
                    </button>
                  </div>
                ) : (
                  <div className="tenant-membership-table-wrapper">
                    <table className="tenant-membership-table">
                      <thead>
                        <tr>
                          <th>Project</th>
                          <th>Professional</th>
                          <th>Purpose</th>
                          <th>Requested Fields</th>
                          <th>Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {requests.map((r) => (
                          <tr key={r.id}>
                            <td>
                              <strong>{getProjectName(r.project)}</strong>
                            </td>

                            <td>{getProfessionalName(r.professional)}</td>

                            <td>
                              <span className="disclosure-purpose-cell">
                                {r.purpose || "-"}
                              </span>
                            </td>

                            <td>
                              <div className="tenant-membership-scope-pills">
                                {getFieldsArray(r).length === 0 ? (
                                  <span className="tenant-membership-muted">
                                    -
                                  </span>
                                ) : (
                                  getFieldsArray(r).map((f, i) => (
                                    <span
                                      key={i}
                                      className="tenant-membership-scope-pill"
                                    >
                                      {f}
                                    </span>
                                  ))
                                )}
                              </div>
                            </td>

                            <td>
                              <div className="tenant-membership-actions">
                                <button
                                  type="button"
                                  className="tenant-membership-edit-btn"
                                  onClick={() => handleEdit(r)}
                                >
                                  Edit
                                </button>

                                <button
                                  type="button"
                                  className="tenant-membership-delete-btn"
                                  onClick={() => handleDelete(r)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="tenant-membership-page-header">
                <div>
                  <h2>
                    {isEditing
                      ? "Edit Disclosure Request"
                      : "Create Disclosure Requests"}
                  </h2>
                </div>

                <button
                  type="button"
                  className="tenant-membership-back-btn"
                  onClick={handleCancel}
                >
                  ← Back
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                {formData.map((r, index) => (
                  <div
                    className="tenant-membership-card"
                    key={index}
                    style={{ marginBottom: "20px" }}
                  >
                    <div className="tenant-membership-form-body">
                      <div className="tenant-membership-record-header">
                        <h4>Request {index + 1}</h4>

                        {!isEditing && formData.length > 1 && (
                          <button
                            type="button"
                            className="tenant-membership-remove-btn"
                            onClick={() => handleRemoveForm(index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className="disclosure-context">
                        <div className="disclosure-context-item">
                          <span className="disclosure-context-label">
                            Project
                          </span>
                          <strong className="disclosure-context-value">
                            {projectLabel || "—"}
                          </strong>
                        </div>

                        <div className="disclosure-context-item">
                          <span className="disclosure-context-label">
                            Professional
                          </span>
                          <strong className="disclosure-context-value">
                            {professionalLabel || "—"}
                          </strong>
                        </div>
                      </div>

                      <div className="tenant-membership-form-grid">
                        <div className="tenant-membership-form-group tenant-membership-form-group--full">
                          <label>
                            Purpose <span>*</span>
                          </label>
                          <textarea
                            className="disclosure-purpose-input"
                            value={r.purpose}
                            onChange={(e) =>
                              handlePurposeChange(index, e.target.value)
                            }
                            placeholder="Describe why these details are being requested"
                            rows={3}
                          />
                        </div>

                        <div className="tenant-membership-form-group tenant-membership-form-group--full">
                          <label>
                            Requested Fields <span>*</span>
                          </label>

                          <div className="tenant-membership-scope-chips">
                            {r.requested_fields.length === 0 &&
                            !r.field_input ? (
                              <span className="tenant-membership-scope-loading">
                                Type a field name and press Enter
                              </span>
                            ) : null}

                            {r.requested_fields.map((field) => (
                              <span
                                className="tenant-membership-scope-chip tenant-membership-scope-chip--active"
                                key={field}
                              >
                                <span>{field}</span>
                                <button
                                  type="button"
                                  className="tenant-membership-scope-chip-remove"
                                  onClick={() =>
                                    removeFieldChip(index, field)
                                  }
                                  aria-label={`Remove ${field}`}
                                >
                                  ×
                                </button>
                              </span>
                            ))}

                            <input
                              type="text"
                              className="tenant-membership-scope-input"
                              value={r.field_input}
                              onChange={(e) =>
                                handleFieldInputChange(index, e.target.value)
                              }
                              onKeyDown={(e) => handleFieldKeyDown(index, e)}
                              onBlur={() => addFieldChip(index)}
                              placeholder={
                                r.requested_fields.length
                                  ? ""
                                  : "e.g. email, phone, address"
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {!isEditing && (
                  <button
                    type="button"
                    className="tenant-membership-add-btn"
                    onClick={handleAddAnother}
                    style={{ marginBottom: "20px" }}
                  >
                    <span>+</span>
                    Add Another Request
                  </button>
                )}

                <div className="tenant-membership-form-actions">
                  <button
                    type="button"
                    className="tenant-membership-cancel-btn"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="tenant-membership-save-btn"
                    disabled={loading}
                  >
                    {loading
                      ? "Saving..."
                      : isEditing
                      ? "Update Request"
                      : "Save Requests"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddDisclosureRequest;